import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import url from '../constants/config';
import ConfirmModal from '../shared/displayComponents/confirmModal/ConfirmModal';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

/* ─── helpers ──────────────────────────────────────────────────────── */

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatDuration = mins => {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
};

/* ─── task status styles ───────────────────────────────────────────── */

const TASK_STATUS_STYLES = {
  not_started:        { bg: '#f1f5f9', color: '#64748b', label: 'Nie rozpoczęto' },
  in_progress:        { bg: '#dbeafe', color: '#1d4ed8', label: 'W trakcie' },
  waiting:            { bg: '#fef9c3', color: '#92400e', label: 'Oczekuje' },
  waiting_athlete:    { bg: '#fef3c7', color: '#b45309', label: 'Czeka sportowiec' },
  waiting_university: { bg: '#e0f2fe', color: '#0369a1', label: 'Czeka uczelnia' },
  waiting_playsport:  { bg: '#ede9fe', color: '#7c3aed', label: 'Czeka PLAYSPORT' },
  to_verify:          { bg: '#d1fae5', color: '#059669', label: 'Do weryfikacji' },
  on_hold:            { bg: '#f1f5f9', color: '#475569', label: 'Wstrzymano' },
  done:               { bg: '#dcfce7', color: '#15803d', label: 'Ukończono' },
};

/* ─── shared table styles ──────────────────────────────────────────── */

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
  fontSize: '13px',
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontSize: '13px',
};

/* ─── time entry status badge ──────────────────────────────────────── */

const StatusBadge = ({ status }) => {
  const map = {
    pending:  { label: 'Oczekuje',     bg: '#fef3c7', color: '#92400e' },
    approved: { label: 'Zatwierdzone', bg: '#dcfce7', color: '#166534' },
    rejected: { label: 'Odrzucone',    bg: '#fee2e2', color: '#991b1b' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

/* ─── main component ───────────────────────────────────────────────── */

const AdminAssistantProfile = () => {
  const { email: rawEmail } = useParams();
  const email = decodeURIComponent(rawEmail);
  const history = useHistory();

  // Profile data
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmLabel: 'Potwierdź' });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }));

  // Rate editing
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  // Players section expand
  const [playersExpanded, setPlayersExpanded] = useState(true);

  // Tasks section — per-player expand
  const [expandedPlayers, setExpandedPlayers] = useState({});

  // Status message for revoke access
  const [revokeStatus, setRevokeStatus] = useState(null);

  // Delete assistant
  const [deletingAssistant, setDeletingAssistant] = useState(false);

  // Time entries
  const [month, setMonth] = useState(currentMonth());
  const [statusFilter, setStatusFilter] = useState('');
  const [timeData, setTimeData] = useState(null);
  const [loadingTime, setLoadingTime] = useState(false);
  const [timeError, setTimeError] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  /* ── load profile ── */
  useEffect(() => {
    axios
      .get(`${url}/admin/assistants/${email}/profile`, {
        headers: getAuthHeader(),
      })
      .then(res => {
        setProfile(res.data);
        setRateInput(res.data.assistant.hourlyRate != null ? String(res.data.assistant.hourlyRate) : '');
      })
      .catch(() => setProfileError('Nie udało się załadować profilu asystenta.'))
      .finally(() => setLoadingProfile(false));
  }, [email]);

  /* ── load time entries ── */
  const fetchTimeEntries = useCallback(async () => {
    setLoadingTime(true);
    setTimeError('');
    try {
      const params = { assistantEmail: email, month };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${url}/admin/time-entries`, {
        headers: getAuthHeader(),
        params,
      });
      setTimeData(res.data);
    } catch {
      setTimeError('Nie udało się załadować wpisów czasu.');
    } finally {
      setLoadingTime(false);
    }
  }, [email, month, statusFilter]);

  useEffect(() => {
    fetchTimeEntries();
  }, [fetchTimeEntries]);

  /* ── rate save ── */
  const saveRate = async () => {
    const val = Number(rateInput);
    if (isNaN(val) || rateInput === '') return;
    setSavingRate(true);
    try {
      await axios.put(
        `${url}/admin/assistants/${email}/rate`,
        { hourlyRate: val },
        { headers: getAuthHeader() }
      );
      setProfile(prev => ({ ...prev, assistant: { ...prev.assistant, hourlyRate: val } }));
      setEditingRate(false);
    } catch {
      // ignore
    } finally {
      setSavingRate(false);
    }
  };

  /* ── reload profile ── */
  const reloadProfile = () => {
    axios
      .get(`${url}/admin/assistants/${email}/profile`, { headers: getAuthHeader() })
      .then(res => {
        setProfile(res.data);
        setRateInput(res.data.assistant.hourlyRate != null ? String(res.data.assistant.hourlyRate) : '');
      })
      .catch(() => {});
  };

  /* ── revoke access ── */
  const revokeAccess = playerId => {
    setConfirmModal({
      show: true,
      title: 'Cofnąć dostęp?',
      message: 'Asystent straci dostęp do tego zawodnika. Tej operacji nie można cofnąć automatycznie.',
      confirmLabel: 'Cofnij dostęp',
      onConfirm: async () => {
        closeConfirm();
        setRevokeStatus(null);
        try {
          await axios.delete(
            `${url}/admin/players/${playerId}/full-access/${email}`,
            { headers: getAuthHeader() }
          );
          reloadProfile();
          setRevokeStatus({ type: 'success', message: 'Dostęp cofnięty.' });
          setTimeout(() => setRevokeStatus(null), 3000);
        } catch (err) {
          setRevokeStatus({ type: 'error', message: err.response?.data?.message || 'Nie udało się cofnąć dostępu.' });
        }
      },
    });
  };

  /* ── delete assistant ── */
  const deleteAssistant = () => {
    setConfirmModal({
      show: true,
      title: 'Usunąć asystenta?',
      message: `Asystent ${assistant ? `${assistant.firstName} ${assistant.lastName}` : ''} zostanie trwale usunięty z systemu. Wszystkie przypisania do zawodników zostaną usunięte, a taski odpięte. Tej operacji nie można cofnąć.`,
      confirmLabel: 'Usuń asystenta',
      onConfirm: async () => {
        closeConfirm();
        setDeletingAssistant(true);
        try {
          await axios.delete(
            `${url}/admin/assistants/${encodeURIComponent(email)}`,
            { headers: getAuthHeader() }
          );
          history.push('/admin/assistants');
        } catch (err) {
          setRevokeStatus({ type: 'error', message: err.response?.data?.message || 'Nie udało się usunąć asystenta.' });
          setDeletingAssistant(false);
        }
      },
    });
  };

  /* ── time entry actions ── */
  const handleApprove = async id => {
    setActionLoading(true);
    try {
      const res = await axios.patch(`${url}/admin/time-entries/${id}/approve`, {}, { headers: getAuthHeader() });
      setTimeData(prev => ({ ...prev, entries: prev.entries.map(e => (e._id === id ? res.data : e)) }));
    } catch {
      setTimeError('Nie udało się zatwierdzić wpisu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async id => {
    setActionLoading(true);
    try {
      const res = await axios.patch(`${url}/admin/time-entries/${id}/reject`, { note: rejectNote }, { headers: getAuthHeader() });
      setTimeData(prev => ({ ...prev, entries: prev.entries.map(e => (e._id === id ? res.data : e)) }));
      setRejectId(null);
      setRejectNote('');
    } catch {
      setTimeError('Nie udało się odrzucić wpisu.');
    } finally {
      setActionLoading(false);
    }
  };

  /* ── render helpers ── */
  const togglePlayer = pid => setExpandedPlayers(prev => ({ ...prev, [pid]: !prev[pid] }));

  /* ── loading / error states ── */
  if (loadingProfile) return <Loader />;
  if (profileError) return <div style={{ padding: '32px', color: '#ef4444' }}>{profileError}</div>;

  const { assistant, assignedPlayers, taskBreakdown, globalTasks, timeEntrySummary } = profile;
  const totalTasks = taskBreakdown.reduce((s, p) => s + p.totalAssigned, 0);
  const doneTasks  = taskBreakdown.reduce((s, p) => s + p.doneTasks, 0);

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1100px' }}>

      {/* ── Back link ── */}
      <button
        type="button"
        onClick={() => history.push('/admin/assistants')}
        style={{ background: 'none', border: 'none', color: '#2e496b', cursor: 'pointer', fontSize: '13px', padding: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        <i className="fas fa-arrow-left" style={{ fontSize: '11px' }} />
        Wróć do listy asystentów
      </button>

      {/* ── Status messages ── */}
      {revokeStatus && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            background: revokeStatus.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: revokeStatus.type === 'success' ? '#166534' : '#991b1b',
            border: `1px solid ${revokeStatus.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          }}
        >
          {revokeStatus.message}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          ① DANE PODSTAWOWE
      ════════════════════════════════════════════════════════ */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '20px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
            {assistant.firstName} {assistant.lastName}
          </h2>
          <div style={{ color: '#64748b', fontSize: '14px' }}>{assistant.email}</div>
          <button
            type="button"
            onClick={deleteAssistant}
            disabled={deletingAssistant}
            style={{
              marginTop: '10px',
              padding: '6px 14px',
              background: '#fee2e2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: deletingAssistant ? 'not-allowed' : 'pointer',
              opacity: deletingAssistant ? 0.6 : 1,
            }}
          >
            <i className="fas fa-trash-alt" style={{ marginRight: '6px' }} />
            {deletingAssistant ? 'Usuwanie...' : 'Usuń asystenta'}
          </button>
        </div>

        {/* KPI badges */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 16px', textAlign: 'center', minWidth: '90px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#0369a1', lineHeight: 1 }}>{assignedPlayers.length}</div>
            <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: 600, marginTop: '4px' }}>zawodników</div>
          </div>
          <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 16px', textAlign: 'center', minWidth: '90px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#b45309', lineHeight: 1 }}>{totalTasks - doneTasks}</div>
            <div style={{ fontSize: '11px', color: '#b45309', fontWeight: 600, marginTop: '4px' }}>tasków w toku</div>
          </div>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 16px', textAlign: 'center', minWidth: '90px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#15803d', lineHeight: 1 }}>{doneTasks}</div>
            <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 600, marginTop: '4px' }}>ukończonych</div>
          </div>
          {timeEntrySummary.pendingCount > 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 16px', textAlign: 'center', minWidth: '90px' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#d97706', lineHeight: 1 }}>{timeEntrySummary.pendingCount}</div>
              <div style={{ fontSize: '11px', color: '#d97706', fontWeight: 600, marginTop: '4px' }}>wpisów do zatwierdzenia</div>
            </div>
          )}
        </div>

        {/* Hourly rate */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Stawka:</span>
          {editingRate ? (
            <>
              <input
                type="number"
                value={rateInput}
                onChange={e => setRateInput(e.target.value)}
                min="0"
                step="0.01"
                style={{ width: '80px', padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
              />
              <span style={{ fontSize: '13px', color: '#64748b' }}>PLN/h</span>
              <button
                type="button"
                onClick={saveRate}
                disabled={savingRate}
                style={{ padding: '5px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                {savingRate ? '...' : 'Zapisz'}
              </button>
              <button
                type="button"
                onClick={() => { setEditingRate(false); setRateInput(assistant.hourlyRate != null ? String(assistant.hourlyRate) : ''); }}
                style={{ padding: '5px 10px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
              >
                Anuluj
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: '14px', fontWeight: 600, color: assistant.hourlyRate != null ? '#1e293b' : '#94a3b8' }}>
                {assistant.hourlyRate != null ? `${assistant.hourlyRate} PLN/h` : '— nie ustawiono'}
              </span>
              <button
                type="button"
                onClick={() => { setEditingRate(true); setRateInput(assistant.hourlyRate != null ? String(assistant.hourlyRate) : ''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '2px 6px', fontSize: '12px' }}
                title="Edytuj stawkę"
              >
                <i className="fas fa-pencil-alt" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          ② PRZYPISANI ZAWODNICY (pełny dostęp)
      ════════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '24px', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setPlayersExpanded(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '14px 20px', background: 'none', border: 'none',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
            Przypisani zawodnicy (pełny dostęp)
            <span style={{ marginLeft: '10px', background: '#e0f2fe', color: '#0369a1', padding: '1px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
              {assignedPlayers.length}
            </span>
          </span>
          <i className={`fas fa-chevron-${playersExpanded ? 'up' : 'down'}`} style={{ color: '#94a3b8', fontSize: '12px' }} />
        </button>

        {playersExpanded && (
          <div style={{ borderTop: '1px solid #e2e8f0' }}>
            {assignedPlayers.length === 0 ? (
              <div style={{ padding: '20px', color: '#94a3b8', fontSize: '14px' }}>
                Brak zawodników z pełnym dostępem.
              </div>
            ) : (
              assignedPlayers.map(p => (
                <div
                  key={p.playerId}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ color: '#2e496b', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    onClick={() => history.push(`/available-players/${p.playerId}?tab=checklist`)}
                    onKeyDown={e => { if (e.key === 'Enter') history.push(`/available-players/${p.playerId}?tab=checklist`); }}
                  >
                    <i className="fas fa-user" style={{ marginRight: '8px', color: '#94a3b8', fontSize: '11px' }} />
                    {p.playerName}
                  </span>
                  <button
                    type="button"
                    onClick={() => revokeAccess(p.playerId)}
                    style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cofnij dostęp
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════
          ③ TASKI — per zawodnik
      ════════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>Taski</span>
          {totalTasks - doneTasks > 0 && (
            <span style={{ background: '#fef3c7', color: '#b45309', padding: '1px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
              {totalTasks - doneTasks} w toku
            </span>
          )}
          {doneTasks > 0 && (
            <span style={{ background: '#dcfce7', color: '#15803d', padding: '1px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
              {doneTasks} ukończonych
            </span>
          )}
        </div>

        {taskBreakdown.length === 0 ? (
          <div style={{ padding: '20px', color: '#94a3b8', fontSize: '14px' }}>Brak przypisanych zadań.</div>
        ) : (
          taskBreakdown.map(p => {
            const isOpen = expandedPlayers[p.playerId] !== false; // expanded by default
            return (
              <div key={p.playerId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                {/* Player header */}
                <button
                  type="button"
                  onClick={() => togglePlayer(p.playerId)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '10px 20px', background: isOpen ? '#f8fafc' : '#fff',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      role="button"
                      tabIndex={0}
                      style={{ fontWeight: 600, fontSize: '13px', color: '#2e496b', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                      onClick={e => { e.stopPropagation(); history.push(`/available-players/${p.playerId}?tab=checklist`); }}
                      onKeyDown={e => { if (e.key === 'Enter') history.push(`/available-players/${p.playerId}?tab=checklist`); }}
                    >
                      {p.playerName}
                    </span>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {p.totalAssigned} zadań{p.doneTasks > 0 ? ` · ${p.doneTasks} ukończonych` : ''}
                    </span>
                  </div>
                  <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: '#94a3b8', fontSize: '11px' }} />
                </button>

                {/* Individual tasks */}
                {isOpen && (
                  <div style={{ padding: '4px 20px 12px 36px', background: '#f8fafc' }}>
                    {p.tasks.map(t => {
                      const style = TASK_STATUS_STYLES[t.status] || TASK_STATUS_STYLES.not_started;
                      return (
                        <div
                          key={t.taskId}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '12px', borderBottom: '1px solid #f1f5f9' }}
                        >
                          <span style={{ flexShrink: 0, background: style.bg, color: style.color, padding: '1px 7px', borderRadius: '8px', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>
                            {style.label}
                          </span>
                          <span style={{ flex: 1, color: '#334155' }}>{t.label}</span>
                          {t.daysSinceUpdate != null && t.status !== 'done' && (
                            <span style={{
                              flexShrink: 0,
                              fontSize: '11px',
                              fontWeight: t.daysSinceUpdate > 2 ? 600 : 400,
                              color: t.daysSinceUpdate > 7 ? '#dc2626' : t.daysSinceUpdate > 2 ? '#f59e0b' : '#94a3b8',
                              whiteSpace: 'nowrap',
                            }}>
                              {t.daysSinceUpdate === 0 ? 'dziś' : `${t.daysSinceUpdate}d temu`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ════════════════════════════════════════════════════════
          ③B ZADANIA GLOBALNE
      ════════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>Zadania globalne</span>
          {globalTasks && globalTasks.length > 0 && (
            <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '1px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
              {globalTasks.length}
            </span>
          )}
        </div>

        {!globalTasks || globalTasks.length === 0 ? (
          <div style={{ padding: '20px', color: '#94a3b8', fontSize: '14px' }}>Brak globalnych zadań.</div>
        ) : (
          <div style={{ padding: '4px 20px 12px 20px', background: '#f8fafc' }}>
            {globalTasks.map(t => {
              const style = TASK_STATUS_STYLES[t.status] || TASK_STATUS_STYLES.not_started;
              const nowMs = Date.now();
              const daysSinceUpdate = t.updatedAt
                ? Math.floor((nowMs - new Date(t.updatedAt).getTime()) / 86400000)
                : null;
              return (
                <div
                  key={t.taskId}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', fontSize: '12px', borderBottom: '1px solid #f1f5f9' }}
                >
                  <span style={{ flexShrink: 0, background: style.bg, color: style.color, padding: '1px 7px', borderRadius: '8px', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {style.label}
                  </span>
                  {t.priority && (
                    <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', background: '#f0f0f0', color: '#475569' }}>
                      {t.priority}
                    </span>
                  )}
                  <span style={{ flex: 1, color: '#334155' }}>{t.label}</span>
                  {t.deadline && (
                    <span style={{ flexShrink: 0, fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {new Date(t.deadline).toLocaleDateString('pl-PL')}
                    </span>
                  )}
                  {daysSinceUpdate != null && t.status !== 'done' && (
                    <span style={{
                      flexShrink: 0,
                      fontSize: '11px',
                      fontWeight: daysSinceUpdate > 2 ? 600 : 400,
                      color: daysSinceUpdate > 7 ? '#dc2626' : daysSinceUpdate > 2 ? '#f59e0b' : '#94a3b8',
                      whiteSpace: 'nowrap',
                    }}>
                      {daysSinceUpdate === 0 ? 'dziś' : `${daysSinceUpdate}d temu`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm || (() => {})}
        onCancel={closeConfirm}
        confirmLabel={confirmModal.confirmLabel || 'Potwierdź'}
        danger
      />

      {/* ════════════════════════════════════════════════════════
          ④ WPISY CZASU / ROZLICZENIA
      ════════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>Wpisy czasu pracy</span>
        </div>

        {/* Filters */}
        <div style={{ padding: '14px 20px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Miesiąc</label>
            <input
              type="month"
              value={month}
              onChange={e => setMonth(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value="">Wszystkie</option>
              <option value="pending">Oczekujące</option>
              <option value="approved">✓ Zatwierdzone</option>
              <option value="rejected">✗ Odrzucone</option>
            </select>
          </div>

          {/* Summary chips */}
          {timeData && timeData.summary.byAssistant.length > 0 && (() => {
            const row = timeData.summary.byAssistant[0];
            return (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                <span style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#0369a1' }}>
                  {formatDuration(row.totalMinutes)}
                </span>
                <span style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#15803d' }}>
                  {(row.totalPay || 0).toFixed(2)} PLN
                </span>
                <span style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', color: '#475569' }}>
                  {row.entryCount} wpisów
                </span>
              </div>
            );
          })()}
        </div>

        {timeError && (
          <div style={{ color: '#dc2626', background: '#fee2e2', padding: '10px 20px', fontSize: '13px' }}>{timeError}</div>
        )}

        {loadingTime && <Loader />}

        {!loadingTime && timeData && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={thStyle}>Zawodnik</th>
                  <th style={thStyle}>Data</th>
                  <th style={thStyle}>Godziny</th>
                  <th style={thStyle}>Czas</th>
                  <th style={thStyle}>Koszt</th>
                  <th style={thStyle}>Notatki</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {timeData.entries.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      Brak wpisów dla wybranego miesiąca.
                    </td>
                  </tr>
                )}
                {timeData.entries.map(entry => (
                  <React.Fragment key={entry._id}>
                    <tr style={{ borderBottom: rejectId === entry._id ? 'none' : '1px solid #f1f5f9' }}>
                      <td style={{ ...tdStyle, fontWeight: 500, color: '#1e293b' }}>{entry.playerName}</td>
                      <td style={tdStyle}>{entry.date}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{entry.startTime} – {entry.endTime}</td>
                      <td style={tdStyle}>{formatDuration(entry.durationMinutes)}</td>
                      <td style={{ ...tdStyle, color: '#16a34a', fontWeight: 600 }}>
                        {entry.totalPay != null ? `${entry.totalPay.toFixed(2)} PLN` : '—'}
                      </td>
                      <td style={{ ...tdStyle, color: '#64748b', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.notes || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge status={entry.status} />
                        {entry.status === 'rejected' && entry.adminNote && (
                          <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '3px' }}>{entry.adminNote}</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {entry.status !== 'approved' && (
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => handleApprove(entry._id)}
                            style={{ padding: '4px 10px', background: '#dcfce7', color: '#166534', border: 'none', borderRadius: '5px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginRight: '4px' }}
                          >
                            ✓ Zatwierdź
                          </button>
                        )}
                        {entry.status !== 'rejected' && (
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => { setRejectId(entry._id); setRejectNote(''); }}
                            style={{ padding: '4px 10px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '5px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            ✗ Odrzuć
                          </button>
                        )}
                      </td>
                    </tr>
                    {rejectId === entry._id && (
                      <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#fff8f8' }}>
                        <td colSpan={8} style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="Powód odrzucenia (opcjonalnie)"
                              value={rejectNote}
                              onChange={e => setRejectNote(e.target.value)}
                              style={{ flex: 1, padding: '6px 10px', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '13px' }}
                            />
                            <button
                              type="button"
                              disabled={actionLoading}
                              onClick={() => handleRejectSubmit(entry._id)}
                              style={{ padding: '6px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                            >
                              Potwierdź
                            </button>
                            <button
                              type="button"
                              onClick={() => setRejectId(null)}
                              style={{ padding: '6px 10px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                            >
                              Anuluj
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAssistantProfile;
